<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';

const props = defineProps({
    tableName: String,
    id: Number,
    isActive: {
        type: Boolean,
        default: false
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const item = ref(null);
const hasGroupAccess = ref(false);

// --- Computed Statuses ---

const isUnpublished = computed(() => item.value?.status === 0);

const isArchived = computed(() => item.value?.isArchived === 1);

const isPending = computed(() => {
    if (!item.value?.validFrom) return false;
    return Date.parse(item.value.validFrom) > Date.now();
});

const isExpired = computed(() => {
    if (!item.value?.validTo) return false;
    return Date.parse(item.value.validTo) <= Date.now();
});

const isExpiring = computed(() => {
    if (!item.value?.validTo) return false;
    const expiry = Date.parse(item.value.validTo);
    // It's expiring if it's in the future, but exists
    return expiry > Date.now();
});

// --- Data Fetching ---

const fetchData = async () => {
    if (!props.id || !props.tableName) return;

    // 1. Get Item Data
    item.value = await store.dispatch('itemData/get', {
        tableName: props.tableName,
        id: props.id
    });

    // 2. Get Group Access Info
    if (item.value) {
        const accessData = await store.dispatch('categories/getTableColumnData', {
            tableName: props.tableName,
            columnName: 'groupAccess'
        });

        hasGroupAccess.value = accessData.some(o => o.itemId === item.value.id);
    }
};

// Re-run whenever ID or Table changes
watch(() => [props.id, props.tableName], fetchData, { immediate: true });
</script>

<template>
    <div
        v-if="hasGroupAccess || isUnpublished || isPending || isExpiring || isExpired || isArchived"
        class="d-inline-flex gap-1"
    >
        <i v-if="hasGroupAccess" class="bi bi-person-fill" title="This page is assigned group access"/>

        <i v-if="isArchived" class="bi bi-archive-fill" :class="{'text-danger': !isActive}" title="Archived"/>

        <i v-if="isUnpublished" class="bi bi-x-circle-fill" :class="{'text-danger': !isActive}" title="Unpublished"/>

        <template v-else>
            <i v-if="isPending" class="bi bi-clock-fill" :class="{'text-info': !isActive}" title="Pending"/>
            <i v-else-if="isExpiring" class="bi bi-clock-fill" :class="{'text-warning': !isActive}" title="Expiring"/>
            <i v-else-if="isExpired" class="bi bi-clock-fill" :class="{'text-danger': !isActive}" title="Expired"/>
        </template>
    </div>
</template>